import { Form, FormikProps, useFormikContext } from 'formik';

import { Button } from '../../../../cdl/Button/Button';
import { Callout } from '../../../../cdl/Callout/Callout';
import { FormikCheckbox } from '../../../../cdl/Checkbox/FormikCheckbox';
import { FormikInput } from '../../../../cdl/Input/FormikInput';
import { Text } from '../../../../cdl/Text/Text';
import { FormikDebug } from '../../../../common/form-elements/formik/FormikDebug';
import { isValidEmailAddress } from '../../../../common/helpers/isValidEmailAddress.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { IconInfoCircle } from '../../../../common/icons/cdl/InfoCircle';
import { FormikTagListInput } from '../../../../common/TagListInput/FormikTagListInput';
import { Box } from '../../../../common/ui/Box';
import { OfferModel } from '../../../model/OfferModel';

import { BasicAccordionWithoutHeader } from './BasicAccordionWithoutHeader';
import { FormikShareOrderReceiversState } from './ShareOrderPopup';

interface FormikShareOrderReceiversContentProps extends FormikProps<FormikShareOrderReceiversState> {
    offer: OfferModel;
    onDismiss: () => void;
}

export const FormikShareOrderFormContent = (props: FormikShareOrderReceiversContentProps) => {
    const { values } = useFormikContext<FormikShareOrderReceiversState>();
    const indent = '28px';

    const valuesToCheck = [
        values.sendToVessel,
        values.sendToCompany,
        values.sendToAgent,
        values.sendToOthers,
        values.copyToSelf,
    ];
    const noCheckboxSelected = valuesToCheck.every((value) => value === false);

    return (
        <Form>
            <Box display="flex" flexDirection="column" rowGap={16} padding={24}>
                <Text variant="title">{translate('shareOrderPopup.title')}</Text>

                <Box>
                    <Text variant="small" color="foreground.muted">
                        {translate('shareOrderPopup.description')}
                    </Text>

                    <BasicAccordionWithoutHeader isOpen={!props.offer.vessel.email && values.sendToVessel}>
                        <Box paddingTop={16}>
                            <Callout
                                variant="accent"
                                description={translate('shareOrderPopup.noMailAddressWarning')}
                                Icon={IconInfoCircle}
                            />
                        </Box>
                    </BasicAccordionWithoutHeader>
                </Box>

                <Box paddingBottom={24}>
                    <Text variant="fieldLabel" color="foreground.muted">
                        {translate('shareOrderPopup.selectRecipients')}
                    </Text>

                    <Box display="flex" flexDirection="column" alignItems="start" rowGap={8} marginTop={4}>
                        <Box alignSelf="stretch">
                            <FormikCheckbox
                                name="sendToVessel"
                                label={translate('shareOrderPopup.sendToVessel')}
                                description={props.offer.vessel.email}
                            />

                            <Box marginLeft={indent}>
                                <BasicAccordionWithoutHeader isOpen={values.sendToVessel && !props.offer.vessel.email}>
                                    <Box paddingBottom={8}>
                                        <FormikInput
                                            label={translate('shareOrderPopup.emailAddress')}
                                            name="vesselEmailAddress"
                                            placeholder={translate('shareOrderPopup.placeholder.emailAddress')}
                                        />
                                    </Box>
                                </BasicAccordionWithoutHeader>
                            </Box>
                        </Box>

                        {props.offer.customer.email ? (
                            <FormikCheckbox
                                name="sendToCompany"
                                label={translate('shareOrderPopup.sendToCompany')}
                                description={props.offer.customer.email}
                            />
                        ) : null}

                        {props.offer.agentInformation?.contact?.emailAddress ? (
                            <FormikCheckbox
                                name="sendToAgent"
                                label={translate('shareOrderPopup.sendToAgent')}
                                description={props.offer.agentInformation.contact.emailAddress}
                            />
                        ) : null}

                        <Box width="100%" display="flex" flexDirection="column" alignItems="flex-start">
                            <FormikCheckbox name="sendToOthers" label={translate('shareOrderPopup.sendToOthers')} />

                            <Box paddingLeft={indent} width="100%">
                                <BasicAccordionWithoutHeader isOpen={values.sendToOthers}>
                                    <FormikTagListInput
                                        name="othersEmailAddresses"
                                        aria-label={translate('shareOrderPopup.otherRecipients')}
                                        validate={(value) => {
                                            if (!isValidEmailAddress(value)) {
                                                return translate('shareOrderPopup.emailAddressError');
                                            }
                                            return null;
                                        }}
                                    />
                                </BasicAccordionWithoutHeader>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box display="flex" justifyContent="space-between" columnGap={8}>
                    <FormikCheckbox name="copyToSelf" label={translate('shareOrderPopup.copyToSelf')} />

                    <Box display="flex" columnGap={8}>
                        <Button type="button" onClick={props.onDismiss}>
                            {translate('btn.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            emphasis="high"
                            disabled={noCheckboxSelected}
                            isLoading={props.isSubmitting}
                        >
                            {translate('shareOrderPopup.sendEmail')}
                        </Button>
                    </Box>
                </Box>
            </Box>
            <FormikDebug />
        </Form>
    );
};
